import React, { createContext, useContext, useEffect, useState } from "react";

const ThemeContext = createContext({ theme: "dark", toggle: () => {} });

const getInitialTheme = () => {
  if (typeof window === "undefined") return "dark";
  const saved = localStorage.getItem("if_theme");
  if (saved === "light" || saved === "dark") return saved;
  // OS preference on first visit
  try {
    if (window.matchMedia?.("(prefers-color-scheme: light)").matches) return "light";
  } catch (err) {
    console.warn("matchMedia unavailable:", err);
  }
  return "dark";
};

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [userOverride, setUserOverride] = useState(
    () => !!localStorage.getItem("if_theme")
  );
  // Tracks whether we're temporarily pinned to dark (public/unauthenticated
  // routes). When true the html class is forced to `dark` regardless of the
  // user's saved preference — but `theme` state remains the user pref so we
  // can restore the moment they return to /app/*.
  const [forcedDark, setForcedDark] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    html.classList.remove("light", "dark");
    html.classList.add(forcedDark ? "dark" : theme);
    if (userOverride && !forcedDark) localStorage.setItem("if_theme", theme);
  }, [theme, userOverride, forcedDark]);

  // Live-sync with OS changes until the user picks manually
  useEffect(() => {
    if (userOverride) return;
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = (e) => setTheme(e.matches ? "light" : "dark");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, [userOverride]);

  const toggle = () => {
    setUserOverride(true);
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  };

  return (
    <ThemeContext.Provider
      value={{ theme: forcedDark ? "dark" : theme, toggle, setTheme, setForcedDark }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
